/**
 * MobileAiCaptureFab — thumb-friendly AI capture FAB for phones.
 *
 * Tap → opens a bottom sheet hosting the same `/api/ai/ask` agent
 * that powers the ⌘K command bar. The voice/long-press recording
 * path was removed (Feb 2026); the FAB is now a single-purpose
 * tap-to-open affordance.
 *
 * Visibility: gated by the `.pd-mobile-only` CSS class which is
 * `display: none` everywhere except `@media (max-width: 480px)`.
 * Desktop users see the existing ⌘K bar instead.
 *
 * Why a separate component instead of reusing `CommandBar`?
 *   - Different UX: bottom sheet vs centered overlay
 *   - Different debounce model: submit-only (mobile) vs auto-fire
 *   - Different size constraints: 96vw single-column vs 980px
 *     two-column
 *   - Sharing `useAiAsk` would force the auto-fire behaviour. We
 *     reuse the same `POST /api/ai/ask` endpoint instead — the
 *     agent contract is the source of truth.
 */
import { useState, useRef, useEffect, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Plus } from 'lucide-react';
import api from '../api';
import { logger } from '../utils/logger';
import { getOrCreateAiSessionId } from '../utils/aiSession';
import { MobileAiCaptureSheet } from './mobile-ai-fab/MobileAiCaptureSheet';

// How long after a successful mutation we keep the sheet open so the
// user can read the confirmation. 2.5s is long enough to read a one-
// line answer but short enough to feel snappy.
const AUTO_CLOSE_AFTER_MUTATION_MS = 2500;

export default function MobileAiCaptureFab() {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [answer, setAnswer] = useState(null);
  const [actions, setActions] = useState([]);
  const [proposals, setProposals] = useState([]);
  const [results, setResults] = useState([]);
  const [error, setError] = useState(null);
  const [naviGoTo, setNaviGoTo] = useState(null);
  // Routing toggle — mirrors QuickAddModal's Type↔Inbox pill.
  //   OFF (default) → POST /api/ai/ask: the AI agent classifies and
  //     either creates the right typed entity or answers a question.
  //   ON            → POST /api/inbox: pure capture, lands in the
  //     triage queue for later classification.
  // Default OFF so existing mobile-FAB users see no behaviour change
  // until they explicitly opt into Inbox routing.
  const [routeToInbox, setRouteToInbox] = useState(false);
  const inputRef = useRef(null);
  const closeTimerRef = useRef(null);
  const location = useLocation();
  const navigate = useNavigate();

  const resetAndClose = useCallback(() => {
    setOpen(false);
    setQuery('');
    setAnswer(null);
    setActions([]);
    setProposals([]);
    setResults([]);
    setError(null);
    setNaviGoTo(null);
    if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
  }, []);

  // Auto-navigate when the agent returns a navigation target.
  useEffect(() => {
    if (!naviGoTo) return;
    try {
      navigate(naviGoTo);
      setOpen(false);
    } catch (err) {
      logger.warn('mobile fab navigate failed:', err?.message);
    }
  }, [naviGoTo, navigate]);

  // Auto-close after a successful mutation so the user gets back to
  // their main view without manual dismissal.
  useEffect(() => {
    if (actions.length > 0 && !loading) {
      closeTimerRef.current = setTimeout(() => {
        resetAndClose();
      }, AUTO_CLOSE_AFTER_MUTATION_MS);
      return () => clearTimeout(closeTimerRef.current);
    }
    return undefined;
  }, [actions.length, loading, resetAndClose]);

  const submit = useCallback(async () => {
    const q = query.trim();
    if (!q || loading) return;
    setLoading(true);
    setError(null);
    setActions([]);
    setProposals([]);
    setResults([]);
    setAnswer(null);
    // Branch on the routing toggle. Inbox path is a single POST that
    // resolves to a friendly answer string + an `actions` chip; the
    // auto-close-on-mutation effect then dismisses the sheet, same as
    // a successful AI-agent mutation.
    if (routeToInbox) {
      try {
        const resp = await api.post('/inbox', { title: q }, { timeout: 15000 });
        const item = resp?.data;
        setAnswer(`Saved to Inbox · "${q.length > 60 ? q.slice(0, 60) + '…' : q}"`);
        setActions([{ kind: 'inbox', id: item?.id, label: 'Inbox · ready for triage' }]);
        try { window.dispatchEvent(new Event('polkdesq:data-mutated')); } catch { /* ignore */ }
      } catch (err) {
        logger.warn('mobile fab /inbox failed:', err?.message);
        setError(err?.code === 'ECONNABORTED'
          ? 'That took longer than expected — tap send to try again.'
          : (err?.response?.data?.detail || 'Save to Inbox failed'));
      } finally {
        setLoading(false);
      }
      return;
    }
    try {
      const resp = await api.post('/ai/ask', {
        query: q,
        // Share the same conversation thread as the ⌘K bar (same
        // helper persists to localStorage) so a user can capture on
        // desktop, follow up on mobile, and the agent has continuity.
        sessionId: getOrCreateAiSessionId(),
      }, { timeout: 25000 });
      const data = resp?.data || {};
      setAnswer(data.answer || null);
      setResults(Array.isArray(data.results) ? data.results : []);
      setActions(Array.isArray(data.actions) ? data.actions : []);
      setProposals(Array.isArray(data.proposals) ? data.proposals : []);
      setNaviGoTo(data.navigate || null);
    } catch (err) {
      logger.warn('mobile fab /ai/ask failed:', err?.message);
      setError(err?.code === 'ECONNABORTED'
        ? 'That took longer than expected — tap send to try again.'
        : (err?.response?.data?.detail || 'AI service hiccuped'));
    } finally {
      setLoading(false);
    }
  }, [query, loading, routeToInbox]);

  const confirmProposal = useCallback(async (proposal) => {
    try {
      const resp = await api.post('/ai/confirm', {
        kind: proposal.kind,
        id: proposal.id,
        action: proposal.action,
        params: proposal.params || null,
      });
      const ok = resp?.data || {};
      setProposals((prev) => prev.filter((p) => !(p.kind === proposal.kind && p.id === proposal.id)));
      setActions((prev) => [...prev, { kind: proposal.kind, id: proposal.id, label: ok.answer || `${proposal.action} done` }]);
      if (ok.answer) setAnswer(ok.answer);
    } catch (err) {
      setError(err?.response?.data?.detail || 'Could not confirm action');
    }
  }, []);

  const selectResult = useCallback((item) => {
    if (!item?.path) return;
    navigate(item.path);
    setOpen(false);
  }, [navigate]);

  // Hide on login/onboarding routes — same convention as QuickCapture.
  if (location.pathname === '/login' || location.pathname === '/onboarding') return null;

  return (
    <>
      <MobileAiCaptureSheet
        open={open}
        onClose={resetAndClose}
        inputRef={inputRef}
        query={query}
        onQueryChange={setQuery}
        onSubmit={submit}
        loading={loading}
        error={error}
        answer={answer}
        proposals={proposals}
        onConfirmProposal={confirmProposal}
        actions={actions}
        results={results}
        onSelectResult={selectResult}
        routeToInbox={routeToInbox}
        onToggleRouteToInbox={() => setRouteToInbox((v) => !v)}
      />

      {/* The FAB — single touch target, single gesture: tap to open
          the AI sheet. (Long-press voice recording was removed Feb
          2026.) Only renders on phones via `.pd-mobile-only`.
          Positioned above the MobileTabBar (~56px tall, z-index 40). */}
      <button
        data-testid="mobile-ai-fab"
        onClick={() => setOpen(true)}
        className="pd-mobile-only pd-mobile-ai-fab fixed right-4 z-[400] rounded-full text-white pd-btn-press"
        style={{
          // Lock the box dimensions inline + strip browser-default
          // button padding/border. Safari adds asymmetric user-agent
          // padding on `<button>` which pulled the `+` icon ~5px off
          // centre; inline `padding: 0` + `appearance: none` makes
          // the centring deterministic across iOS / Chrome / Firefox.
          width: 56,
          height: 56,
          padding: 0,
          border: 'none',
          margin: 0,
          appearance: 'none',
          WebkitAppearance: 'none',
          bottom: 'calc(56px + 24px + env(safe-area-inset-bottom, 0px))',
          background: 'var(--pd-focus)',
          boxShadow: '0 10px 28px rgba(37,99,235,0.42), 0 4px 10px rgba(15,17,23,0.18)',
          WebkitTouchCallout: 'none',
          WebkitUserSelect: 'none',
          userSelect: 'none',
          transition: 'transform 140ms cubic-bezier(0.2, 0.8, 0.2, 1), background 160ms ease, box-shadow 200ms ease',
        }}
        aria-label="Open AI capture"
      >
        <Plus className="w-6 h-6" />
      </button>
    </>
  );
}
